import React, { useState, useEffect, useRef } from 'react';
import { _T, C } from '../../shared/theme';
import { s_PB, s_GB, s_GC } from '../../shared/styles';
import { getGameSession, joinGameSession, setGameSessionReady } from '../../shared/dbService';
import Avatar from '../../components/ui/Avatar';
import StatusDot from '../../components/ui/StatusDot';

/**
 * GameRoom — waiting lobby for multiplayer game sessions.
 *
 * Props:
 *   token        {string}  — session token
 *   lang         {string}  — 'en' | 'nl'
 *   currentUser  {object}  — { id, name }
 *   isInviter    {boolean} — true if this user created the session
 *   gameTitle    {string}
 *   gameIcon     {string}
 *   onStart(partnerUser, mode) — called when both players are ready
 *   onLeave()    — back button
 */
function GameRoom({ token, lang, currentUser, isInviter, gameTitle, gameIcon, onStart, onLeave }) {
  const nl = lang === 'nl';

  const [sessionState, setSessionState] = useState(null);
  const [hasJoined, setHasJoined]       = useState(isInviter); // inviter is always "in"
  const [isReady, setIsReady]           = useState(false);
  const [readyLoading, setReadyLoading] = useState(false);
  const [error, setError]               = useState(null);

  const pollRef    = useRef(null);
  const startedRef = useRef(false); // guard: only call onStart once

  // ── Mount: fetch immediately then poll ────────────────────────────────────
  useEffect(() => {
    let mounted = true;

    const tick = async () => {
      const data = await getGameSession(token);
      if (!mounted) return;
      if (!data || data.error) { setError(nl ? 'Sessie niet gevonden.' : 'Session not found.'); return; }
      setSessionState(data);

      // Auto-join for joiner on first load
      if (!isInviter && !hasJoined && !data.joinerId) {
        const result = await joinGameSession(token, currentUser.id, currentUser.name);
        if (mounted && result && !result.error) {
          setHasJoined(true);
          setSessionState(result);
        }
      } else if (!isInviter && !hasJoined) {
        setHasJoined(true);
      }

      // Check if both ready → launch
      if (!startedRef.current && data.inviterReady && data.joinerReady) {
        startedRef.current = true;
        clearInterval(pollRef.current);
        const partnerId   = isInviter ? data.joinerId   : data.inviterId;
        const partnerName = isInviter ? data.joinerName : data.inviterName;
        if (onStart) onStart({ id: partnerId, name: partnerName }, data.mode);
      }
    };

    tick();
    pollRef.current = setInterval(tick, 3000);

    return () => {
      mounted = false;
      clearInterval(pollRef.current);
    };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Watch sessionState for both-ready transition ──────────────────────────
  useEffect(() => {
    if (!sessionState) return;
    if (startedRef.current) return;
    if (sessionState.inviterReady && sessionState.joinerReady) {
      startedRef.current = true;
      clearInterval(pollRef.current);
      const partnerId   = isInviter ? sessionState.joinerId   : sessionState.inviterId;
      const partnerName = isInviter ? sessionState.joinerName : sessionState.inviterName;
      if (onStart) onStart({ id: partnerId, name: partnerName }, sessionState.mode);
    }
  }, [sessionState]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Ready button handler ───────────────────────────────────────────────────
  const handleReady = async () => {
    if (readyLoading || isReady) return;
    setReadyLoading(true);
    const result = await setGameSessionReady(token, currentUser.id, isInviter ? 'inviter' : 'joiner');
    setReadyLoading(false);
    if (result && !result.error) {
      setIsReady(true);
      setSessionState(result);
    }
  };

  // ── Derived display values ─────────────────────────────────────────────────
  const inviterName    = sessionState?.inviterName    || (isInviter ? currentUser.name : '...');
  const inviterPicture = sessionState?.inviterPicture || null;
  const inviterColor   = sessionState?.inviterColor   || '#C8A84B';
  const inviterStatus  = sessionState?.inviterStatus  || 'offline';

  const joinerName    = sessionState?.joinerName    || (!isInviter ? currentUser.name : null);
  const joinerPicture = sessionState?.joinerPicture || null;
  const joinerColor   = sessionState?.joinerColor   || '#C8A84B';
  const joinerStatus  = sessionState?.joinerStatus  || 'offline';

  const joinerJoined    = !!(sessionState?.joinerId);
  const inviterReady    = sessionState?.inviterReady    || false;
  const joinerReady     = sessionState?.joinerReady     || false;
  const inviteeSeen     = sessionState?.inviteeSeen     || false;
  const inviteeDeclined = sessionState?.inviteeDeclined || false;
  const myReady      = isInviter ? inviterReady : joinerReady;
  const otherReady   = isInviter ? joinerReady  : inviterReady;

  // Ready button enabled: inviter can press once joiner has joined; joiner can always press
  const canReady = isInviter ? joinerJoined : true;

  // ── Styles ─────────────────────────────────────────────────────────────────
  const cardStyle = {
    ...s_GC({ padding: '14px 16px', marginBottom: 10 }),
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  };

  const badgeStyle = (color) => ({
    fontSize: 10,
    fontWeight: 700,
    color,
    background: color + '22',
    border: `1px solid ${color}55`,
    borderRadius: 8,
    padding: '2px 8px',
    letterSpacing: 0.5,
  });

  if (error) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
        <div style={{ color: C.silver, marginBottom: 20 }}>{error}</div>
        <button style={s_GB({ fontSize: 13 })} onClick={onLeave}>
          {nl ? '← Terug' : '← Back'}
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '0 0 8px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
        <button
          onClick={onLeave}
          style={{ background: 'none', border: 'none', color: C.silver, cursor: 'pointer', fontSize: 20, lineHeight: 1, padding: '2px 4px' }}
          aria-label={nl ? 'Terug' : 'Back'}
        >
          ←
        </button>
        <div style={{ fontSize: 26 }}>{gameIcon}</div>
        <div>
          <div style={{ fontFamily: 'Georgia,serif', fontSize: 16, color: '#EEF3FF' }}>
            {nl ? 'Spelkamer' : 'Game Room'}
          </div>
          <div style={{ fontSize: 11, color: C.silver }}>{gameTitle}</div>
        </div>
      </div>

      {/* Subtitle */}
      <div style={{ fontSize: 11, color: C.silver, letterSpacing: 1, marginBottom: 10 }}>
        {nl ? 'SPELERS' : 'PLAYERS'}
      </div>

      {/* Inviter slot */}
      <div style={cardStyle}>
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <Avatar user={{ id: sessionState?.inviterId || 'inv', name: inviterName, color: inviterColor }} size={42} profileImage={inviterPicture} />
          <span style={{ position: 'absolute', bottom: 1, right: 1 }}><StatusDot status={inviterStatus} size={11} /></span>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ color: '#EEF3FF', fontSize: 13, fontWeight: 600 }}>
            {inviterName}
            {isInviter && (
              <span style={{ color: C.silver, fontWeight: 400, fontSize: 11 }}> ({nl ? 'jij' : 'you'})</span>
            )}
          </div>
          <div style={{ fontSize: 11, color: C.silver, marginTop: 2 }}>
            {nl ? 'Uitnodiger' : 'Host'}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          <span style={badgeStyle(C.teal)}>{nl ? 'In kamer' : 'In room'}</span>
          {inviterReady && <span style={badgeStyle(_T.accent)}>{nl ? 'Klaar' : 'Ready'}</span>}
        </div>
      </div>

      {/* Joiner slot */}
      <div style={cardStyle}>
        {joinerJoined ? (
          <>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <Avatar user={{ id: sessionState?.joinerId || 'joi', name: joinerName, color: joinerColor }} size={42} profileImage={joinerPicture} />
              <span style={{ position: 'absolute', bottom: 1, right: 1 }}><StatusDot status={joinerStatus} size={11} /></span>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: '#EEF3FF', fontSize: 13, fontWeight: 600 }}>
                {joinerName}
                {!isInviter && (
                  <span style={{ color: C.silver, fontWeight: 400, fontSize: 11 }}> ({nl ? 'jij' : 'you'})</span>
                )}
              </div>
              <div style={{ fontSize: 11, color: C.silver, marginTop: 2 }}>
                {nl ? 'Vriend' : 'Friend'}
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
              <span style={badgeStyle(C.teal)}>{nl ? 'In kamer' : 'In room'}</span>
              {joinerReady && <span style={badgeStyle(_T.accent)}>{nl ? 'Klaar' : 'Ready'}</span>}
            </div>
          </>
        ) : (
          <>
            <div style={{ width: 38, height: 38, borderRadius: '50%', background: inviteeDeclined ? 'rgba(192,80,80,0.15)' : _T.bg4, border: `1.5px dashed ${inviteeDeclined ? '#C05050' : inviteeSeen ? _T.accent : _T.cardBorderBot}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 16 }}>{inviteeDeclined ? '❌' : inviteeSeen ? '👀' : '⏳'}</span>
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ color: inviteeDeclined ? '#C05050' : inviteeSeen ? _T.accent : C.silver, fontSize: 13, fontStyle: inviteeDeclined ? 'normal' : 'italic', fontWeight: inviteeDeclined ? 600 : 400 }}>
                {inviteeDeclined
                  ? (nl ? 'Uitnodiging geweigerd' : 'Invitation declined')
                  : inviteeSeen
                    ? (nl ? 'Heeft de uitnodiging gezien...' : 'Seen the invite...')
                    : (nl ? 'Wachten op vriend...' : 'Waiting for friend...')}
              </div>
              {inviteeSeen && !inviteeDeclined && (
                <div style={{ fontSize: 10, color: C.silver, marginTop: 2 }}>
                  {nl ? 'Wacht op reactie' : 'Awaiting response'}
                </div>
              )}
            </div>
            {!inviteeDeclined && <div style={{ width: 12, height: 12, borderRadius: '50%', background: inviteeSeen ? _T.accent : C.silver, opacity: inviteeSeen ? 0.8 : 0.4, animation: 'pulse 1.5s infinite' }} />}
          </>
        )}
      </div>

      {/* Ready button / status */}
      <div style={{ marginTop: 20 }}>
        {myReady ? (
          <div style={{ textAlign: 'center', padding: '14px', background: _T.bg4, borderRadius: 14, border: `1px solid ${_T.accent}44` }}>
            <div style={{ color: _T.accent, fontWeight: 700, fontSize: 14, marginBottom: 4 }}>
              {nl ? '✓ Jij bent klaar!' : '✓ You are ready!'}
            </div>
            <div style={{ color: C.silver, fontSize: 12 }}>
              {otherReady
                ? (nl ? 'Beide klaar — starten...' : 'Both ready — launching...')
                : (nl ? 'Wachten op de andere speler...' : 'Waiting for the other player...')}
            </div>
          </div>
        ) : (
          <button
            style={s_PB({ width: '100%', fontSize: 14, opacity: (canReady && !readyLoading) ? 1 : 0.4 })}
            disabled={!canReady || readyLoading}
            onClick={handleReady}
          >
            {readyLoading
              ? (nl ? 'Bezig...' : 'Loading...')
              : (nl ? '✓ Ik ben klaar!' : '✓ I\'m Ready!')}
          </button>
        )}
      </div>

      {/* Waiting hint */}
      {!joinerJoined && (
        <div style={{ marginTop: 14, padding: '10px 14px', background: `${_T.accent}10`, borderRadius: 12, border: `1px solid ${_T.accent}22` }}>
          <div style={{ fontSize: 12, color: C.silver, lineHeight: 1.5 }}>
            {nl
              ? '💡 Je vriend ontvangt een melding. Zodra hij/zij aansluit, kun je beiden op klaar drukken.'
              : '💡 Your friend received a notification. Once they join, you can both press Ready.'}
          </div>
        </div>
      )}

      {/* Pulse keyframe via inline style injection */}
      <style>{`@keyframes pulse { 0%,100%{opacity:0.4} 50%{opacity:1} }`}</style>
    </div>
  );
}

export default GameRoom;
